import { useEffect, useState } from 'react';
import {
    HERO_MONTAGE_CLIPS,
    HERO_MONTAGE_DEFAULT_DURATION,
    HERO_MONTAGE_POSTER,
} from '../../config/heroMontage';

/**
 * Editorial hero montage.
 *
 * Crossfades through a curated playlist of verified stills with a slow
 * Ken Burns drift (disabled when the visitor prefers reduced motion).
 * Config lives in /src/config/heroMontage.js — one-line edits to swap frames.
 */
export default function HeroVideoMontage({ children, clips = HERO_MONTAGE_CLIPS, poster = HERO_MONTAGE_POSTER }) {
    const [i, setI] = useState(0);
    const [labelVisible, setLabelVisible] = useState(true);
    const total = clips.length;
    const current = clips[i] || {};
    const duration = current.duration || HERO_MONTAGE_DEFAULT_DURATION;

    const reducedMotion = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    useEffect(() => {
        if (total < 2 || reducedMotion) return;
        setLabelVisible(true);
        const labelTimer = setTimeout(() => setLabelVisible(false), 4200);
        const clipTimer = setTimeout(() => setI((v) => (v + 1) % total), duration);
        return () => { clearTimeout(labelTimer); clearTimeout(clipTimer); };
    }, [i, duration, total, reducedMotion]);

    if (total === 0) {
        return (
            <div className="relative w-full h-full bg-brand-navy">
                <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
                <div className="relative z-10 w-full h-full">{children}</div>
            </div>
        );
    }

    return (
        <div className="relative w-full h-full overflow-hidden bg-brand-navy">
            {clips.map((clip, idx) => (
                <img
                    key={clip.src}
                    src={clip.src}
                    alt=""
                    loading={idx === 0 ? 'eager' : 'lazy'}
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                        opacity: idx === i ? 1 : 0,
                        transition: 'opacity 1400ms ease',
                        transform: reducedMotion ? 'none' : (idx === i ? 'scale(1.07)' : 'scale(1.0)'),
                        transitionProperty: 'opacity, transform',
                        transitionDuration: `1400ms, ${duration + 1600}ms`,
                        transitionTimingFunction: 'ease, linear',
                    }}
                />
            ))}

            {/* Cinematic scrims */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(11,31,54,0.55) 0%, rgba(11,31,54,0.22) 35%, rgba(11,31,54,0.72) 100%)' }}
                aria-hidden="true"
            />
            <div
                className="absolute inset-x-0 top-0 h-24 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(11,31,58,0.55), rgba(11,31,58,0))' }}
                aria-hidden="true"
            />

            {/* Chapter label — top-right, desktop only (collides with the overline on small screens) */}
            <div
                className="absolute top-24 right-6 lg:right-10 z-10 pointer-events-none hidden md:block"
                style={{ opacity: labelVisible ? 1 : 0, transition: 'opacity 900ms ease' }}
                data-testid="hero-chapter"
            >
                <div className="text-right">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/60 font-mono-data">
                        {String(i + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                    </div>
                    <div className="text-[13px] mt-1.5 text-white/90 font-editorial italic">
                        {current.label}
                    </div>
                </div>
            </div>

            {/* Progress dashes — bottom-right, desktop only */}
            <div className="absolute bottom-6 right-6 lg:right-10 z-10 hidden md:flex items-center gap-2 pointer-events-none">
                {clips.map((_, idx) => (
                    <span
                        key={idx}
                        className="block w-6 h-px transition-colors duration-500"
                        style={{ background: idx === i ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)' }}
                    />
                ))}
            </div>

            {/* Hero content passed as children */}
            <div className="relative z-10 w-full h-full">{children}</div>
        </div>
    );
}
